import React, { useState } from 'react';
import { ArrowLeft, Lock, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUpdatePassword } from '../../hooks/profileQueries';
import ErrorModal from "../Modal/ErrorModal";

const ChangePasswordView = ({ onBack }) => {
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [error, setError] = useState('');
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [success, setSuccess] = useState(false);
  

  // Use the mutation hook
  const updatePasswordMutation = useUpdatePassword({
  onSuccess: () => {
    setSuccess(true);
    setPasswords({ current: '', new: '', confirm: '' });

    setTimeout(() => {
      window.location.reload();
    }, 2000);
  },
  onError: (error) => {
    setErrorModal({
      isOpen: true,
      message: error.message || 'Failed to update password',
    });
  }
});

  const validatePasswords = () => {
    if (!passwords.current) return 'Please enter your current password';
    if (!passwords.new) return 'Please enter a new password';
    if (!passwords.confirm) return 'Please confirm your new password';
    if (passwords.new.length < 8 || passwords.new.length > 50) {
      return 'New password must be between 8 and 50 characters long';
    }
    if (passwords.new !== passwords.confirm) return 'New passwords do not match';
    if (passwords.current === passwords.new) return 'New password must be different from current password';
    return '';
  };

  const handleSave = async () => {
    const validationError = validatePasswords();
    if (validationError) {
      setErrorModal({ isOpen: true, message: validationError });
      return;
    }
    try {
      await updatePasswordMutation.mutateAsync({
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
    } catch (err) {
      // onError above already surfaces the message via errorModal;
      // this catch exists purely to prevent an unhandled rejection.
    }
  };

  const handleCancel = () => {
    setPasswords({ current: '', new: '', confirm: '' });
    setSuccess(false);
    onBack();
  };

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
    if (success) setSuccess(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 6) return { strength: 1, label: 'Weak', color: 'bg-red-500' };
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const strength = [hasLetters, hasNumbers, hasSpecial].filter(Boolean).length;
    if (strength === 1) return { strength: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (strength === 2) return { strength: 3, label: 'Good', color: 'bg-blue-500' };
    return { strength: 4, label: 'Strong', color: 'bg-green-500' };
  };

  const newPasswordStrength = getPasswordStrength(passwords.new);
  const isLoading = updatePasswordMutation.isPending;

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navbar */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-1">
          <button
            onClick={handleCancel}
            className="p-2 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Change Password</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-500" />
              <p className="text-sm text-green-700 font-medium">
                Password updated successfully! Refreshing page...
              </p>
            </div>
          </div>
        )}

        {/* Password Form */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="space-y-4">
            {/* Current Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwords.current}
                  onChange={(e) => handlePasswordChange('current', e.target.value)}
                  placeholder="Enter current password"
                  className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wider"
                  disabled={isLoading}
                >
                  {showPasswords.current ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwords.new}
                  onChange={(e) => handlePasswordChange('new', e.target.value)}
                  placeholder="Enter new password"
                  className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wider"
                  disabled={isLoading}
                >
                  {showPasswords.new ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {passwords.new && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500">Password strength</span>
                    <span className={`text-xs font-medium ${
                      newPasswordStrength.label === 'Weak' ? 'text-red-500' :
                      newPasswordStrength.label === 'Fair' ? 'text-yellow-500' :
                      newPasswordStrength.label === 'Good' ? 'text-blue-500' : 'text-green-500'
                    }`}>
                      {newPasswordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full transition-all duration-300 ${newPasswordStrength.color}`}
                      style={{ width: `${(newPasswordStrength.strength / 4) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwords.confirm}
                  onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                  placeholder="Confirm new password"
                  className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wider"
                  disabled={isLoading}
                >
                  {showPasswords.confirm ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {passwords.confirm && (
                <p className={`text-xs mt-1 ${
                  passwords.new === passwords.confirm ? 'text-green-600' : 'text-red-600'
                }`}>
                  {passwords.new === passwords.confirm ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>

            {/* Info Note */}
            <div className="bg-white rounded-lg p-3 border border-gray-400">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>Password Requirements:</strong> Enter your current password and the new password you want to set. 
                Your new password must be <strong>between 8 and 50 characters</strong> long and different from your current password.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !passwords.current || !passwords.new || !passwords.confirm}
            className="flex-1 px-4 py-3 bg-[#b0d284] text-black rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default ChangePasswordView;